import { company } from '~/company/Company';
import { normalizeDeliveryMethod } from '~/utils/normalizeDeliveryMethod';

import type {
  ETACalculation,
  ETACost,
  ETADeliveryMethod,
  ETAMechanicsAvailability,
  ETAMethodMechanics,
  ETASlotCalculation,
  ETAZone,
} from './ETA';
import { DeliveryMechanic, DeliveryMethods } from './enums';
import type {
  GetETAResponse,
  MethodMechanics,
  WarehouseDeliveryMethods,
} from './v3/ETA';

/** Converts pence to the display string kept alongside every price. */
type ToPounds = (pence: number) => string;

type ETAWarehouseMethods = ETACalculation['warehouse']['deliveryMethods'];

const withPounds = (
  cost: Pick<
    ETACost,
    'shipping' | 'threshold' | 'serviceFee' | 'serviceFeeThreshold'
  >,
  toPounds: ToPounds,
): ETACost => ({
  ...cost,
  shippingPounds: toPounds(cost.shipping),
  thresholdPounds: toPounds(cost.threshold),
  serviceFeeShippingPounds: toPounds(cost.serviceFee),
  serviceFeeThresholdPounds: toPounds(cost.serviceFeeThreshold),
});

/**
 * Clamps missing or negative amounts to zero. Applied on the v1 slot path only,
 * which is where such values have been observed.
 */
const clampCost = (cost: ETACost): ETACost => ({
  ...cost,
  ...(cost.shipping > 0 ? {} : { shipping: 0, shippingPounds: '0' }),
  ...(cost.threshold > 0 ? {} : { threshold: 0, thresholdPounds: '0' }),
  ...(cost.serviceFee > 0
    ? {}
    : { serviceFee: 0, serviceFeeShippingPounds: '0' }),
  ...(cost.serviceFeeThreshold > 0
    ? {}
    : { serviceFeeThreshold: 0, serviceFeeThresholdPounds: '0' }),
});

const EMPTY_COST: ETACost = {
  shipping: 0,
  shippingPounds: '0',
  threshold: 0,
  thresholdPounds: '0',
  serviceFee: 0,
  serviceFeeShippingPounds: '0',
  serviceFeeThreshold: 0,
  serviceFeeThresholdPounds: '0',
};

const toMethodMechanics = (mechanics: MethodMechanics): ETAMethodMechanics =>
  Object.entries(mechanics).reduce<ETAMethodMechanics>(
    (memo, [code, mechanic]) => {
      memo[code as DeliveryMechanic] = {
        orderAmountThreshold: mechanic.orderAmountThreshold,
        schedule: mechanic.schedule?.value ?? [],
      };

      return memo;
    },
    {},
  );

/**
 * Warehouse-level availability, which does NOT mirror the mechanics one to one:
 *
 * - `ON_DEMAND` holds the availability of whichever mechanic delivers the
 *   *requested* method immediately — the pickup mechanic for click-and-collect,
 *   the on-demand one otherwise. Readers (`isExpressAvailableNow`,
 *   `expressDelivery`, and everything downstream of them) ask this one question:
 *   "can the customer get the order right now?", and for a pickup method the
 *   pickup mechanic is the answer.
 * - `SCHEDULED` is derived from slot presence rather than taken from the
 *   response: `availableNow` means "there is a slot to pick".
 *
 * Both rules are carried over verbatim from the store code this replaced. They
 * are load-bearing: express availability gates product limits, cart banners and
 * the checkout delivery selector.
 */
const toWarehouseAvailability = (
  deliveryMethods: WarehouseDeliveryMethods,
  requestedMethod: DeliveryMethods,
): ETAMechanicsAvailability =>
  Object.values(deliveryMethods).reduce<ETAMechanicsAvailability>(
    (memo, method) => {
      const immediate =
        requestedMethod === DeliveryMethods.CLICK_AND_COLLECT
          ? method.deliveryMechanics[DeliveryMechanic.PICKUP]
          : method.deliveryMechanics[DeliveryMechanic.ON_DEMAND];

      if (immediate?.availability) {
        memo[DeliveryMechanic.ON_DEMAND] = immediate.availability;
      }

      const scheduled = method.deliveryMechanics[DeliveryMechanic.SCHEDULED];

      if (scheduled) {
        const slots = Object.values(scheduled.deliverySlots ?? {}).flat();

        memo[DeliveryMechanic.SCHEDULED] = {
          availableNow: !!slots.length,
          nextAvailabilityDay: 7,
          localTime: '',
          opening: '',
          closing: '',
        };
      }

      return memo;
    },
    {},
  );

/**
 * Projects the v3 ETA response onto {@link ETACalculation}.
 *
 * `duration` follows the requested method: only one can be shown at a time, and
 * every caller reads it for the method the customer is currently on.
 *
 * `deliverySlots` defaults to the courier zone slots (SCHEDULED mechanic) of
 * whichever method carries them, same as `master`. When the customer requests
 * click-and-collect, `company.hasPickupSlots` is Heimkaup-only, and its PICKUP
 * mechanic publishes warehouse pickup windows, those override the courier slots and
 * `deliverySlotsArePickup` is set so consumers know not to fall back to courier
 * slots as pickup times. The company check is not belt-and-braces: the backend
 * response carries no scoping of its own — an absent `pickupSlotsEnabled` reads as
 * enabled — so this is what stops another tenant's `deliverySlots` from being
 * silently replaced if its PICKUP mechanic ever publishes them.
 */
export const toCalculation = (
  { warehouse }: GetETAResponse,
  requestedMethod: DeliveryMethods,
  toPounds: ToPounds,
): ETACalculation => {
  const cost: ETACalculation['cost'] = {};
  const paymentMethods: ETACalculation['paymentMethods'] = {};
  const zone: Partial<Record<DeliveryMethods, ETAZone>> = {};
  const deliveryMethods: ETAWarehouseMethods = {};

  let courierSlots: ETACalculation['deliverySlots'] | undefined;
  let pickupSlots: ETACalculation['deliverySlots'] | undefined;
  const duration = { min: 0, max: 0, range: '0 - 0' };

  for (const method of Object.values(warehouse.deliveryMethods)) {
    const type = method.code.toUpperCase() as DeliveryMethods;
    const mechanics = method.deliveryMechanics;

    // A method is priced by whichever mechanic actually delivers it: pickup
    // methods have no on-demand mechanic and vice versa.
    const priced =
      mechanics[DeliveryMechanic.ON_DEMAND] ??
      mechanics[DeliveryMechanic.PICKUP];

    deliveryMethods[type] = {
      type,
      cost: priced?.cost ? withPounds(priced.cost, toPounds) : EMPTY_COST,
      paymentMethods: method.zone
        .paymentMethods as ETADeliveryMethod['paymentMethods'],
      deliveryMechanics: toMethodMechanics(mechanics),
    };

    cost[type] = deliveryMethods[type]?.cost;
    paymentMethods[type] = deliveryMethods[type]?.paymentMethods;
    zone[type] = method.zone;

    if (type === requestedMethod && priced?.duration) {
      duration.min = priced.duration.min;
      duration.max = priced.duration.max;
      duration.range = `${priced.duration.min} - ${priced.duration.max}`;
    }

    const scheduledSlots = mechanics[DeliveryMechanic.SCHEDULED]?.deliverySlots;

    if (scheduledSlots) {
      courierSlots = scheduledSlots;
    }

    const pickup = mechanics[DeliveryMechanic.PICKUP];

    if (
      type === DeliveryMethods.CLICK_AND_COLLECT &&
      company.hasPickupSlots &&
      pickup?.pickupSlotsEnabled !== false &&
      pickup?.deliverySlots
    ) {
      pickupSlots = pickup.deliverySlots;
    }
  }

  const resolvedPickupSlots =
    requestedMethod === DeliveryMethods.CLICK_AND_COLLECT
      ? pickupSlots
      : undefined;
  const deliverySlots = resolvedPickupSlots ?? courierSlots ?? {};

  return {
    cost,
    paymentMethods,
    duration,
    deliverySlots,
    deliverySlotsArePickup: Boolean(resolvedPickupSlots),
    warehouse: {
      id: warehouse.id,
      code: warehouse.code,
      address: warehouse.address,
      location: warehouse.location,
      zone,
      availability: {
        ...warehouse.availability,
        deliveryMechanics: toWarehouseAvailability(
          warehouse.deliveryMethods,
          requestedMethod,
        ),
      },
      deliveryMethods,
    },
  };
};

const LEGACY_TO_MECHANIC = {
  SLOT: DeliveryMechanic.SCHEDULED,
  ON_DEMAND: DeliveryMechanic.ON_DEMAND,
} as const;

/**
 * Projects the v1 `eta-slot` response onto {@link ETACalculation}, translating
 * the two names v1 still uses: `SLOT` for the scheduled mechanic, and
 * `JIFFY_DELIVERY` for courier delivery.
 *
 * TODO: delete together with {@link ETASlotCalculation} once slots are served
 * over v3 — whether the ETA service already exposes `eta/v3/api/eta-slot` has
 * not been confirmed with the backend.
 */
export const slotToCalculation = (
  data: ETASlotCalculation,
  toPounds: ToPounds,
): ETACalculation => {
  const cost: ETACalculation['cost'] = {};
  const paymentMethods: ETACalculation['paymentMethods'] = {};
  const zone: Partial<Record<DeliveryMethods, ETAZone>> = {};
  const deliveryMethods: ETAWarehouseMethods = {};

  for (const method of data.warehouse.deliveryMethods) {
    const type = normalizeDeliveryMethod(method.type);

    if (deliveryMethods[type]) {
      continue;
    }

    const mechanics = Object.values(method.deliveryMechanics ?? {}).at(0) ?? {};

    deliveryMethods[type] = {
      type,
      cost: clampCost(withPounds(method.cost, toPounds)),
      paymentMethods: method.paymentMethods,
      deliveryMechanics: Object.entries(mechanics).reduce<ETAMethodMechanics>(
        (memo, [legacy, mechanic]) => {
          const code =
            LEGACY_TO_MECHANIC[legacy as keyof typeof LEGACY_TO_MECHANIC];

          if (code && mechanic) {
            memo[code] = {
              orderAmountThreshold: mechanic.order_amount_threshold,
              schedule: mechanic.schedule,
            };
          }

          return memo;
        },
        {},
      ),
    };

    cost[type] = deliveryMethods[type]?.cost;
    paymentMethods[type] = method.paymentMethods;
    zone[type] = data.warehouse.zone;
  }

  const { deliveryMechanics, ...warehouseAvailability } =
    data.warehouse.availability;

  return {
    cost,
    paymentMethods,
    deliverySlots: data.deliverySlots,
    highDemand: data.highDemand,
    duration: {
      min: data.duration?.min ?? 0,
      max: data.duration?.max ?? 0,
      range: `${data.duration?.min || 0} - ${data.duration?.max || 0}`,
    },
    warehouse: {
      id: data.warehouse.id,
      code: data.warehouse.code,
      address: data.warehouse.address,
      location: data.warehouse.location,
      zone,
      deliveryMethods,
      availability: {
        ...warehouseAvailability,
        deliveryMechanics: Object.entries(
          deliveryMechanics,
        ).reduce<ETAMechanicsAvailability>((memo, [legacy, value]) => {
          const code =
            LEGACY_TO_MECHANIC[legacy as keyof typeof LEGACY_TO_MECHANIC];

          if (code && value) {
            memo[code] = value;
          }

          return memo;
        }, {}),
      },
    },
  };
};
